import { setLogStyle, useLogStyle } from 'config/LogConfig';
import { dangerColor, infoColor, successColor, warningColor } from 'variables/color';
import DV from 'variables/DV';
import { getPermantlyIP } from "../variables/utils";

export default class LocalStorage {
  static clearLocal = () => {
    console.log(useLogStyle + 'LocalStorage CLEAR', setLogStyle(warningColor));
    try {
      localStorage.clear();
    } catch (error) {
      console.log(
        useLogStyle + 'LocalStorage CLEAR \n ERROR:',
        setLogStyle(dangerColor),
        error.message,
      );
    }
  };

  static removeLocal = (key) => {
    console.log(useLogStyle + 'LocalStorage REMOVE ' + key, setLogStyle(warningColor));
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.log(
        useLogStyle + 'LocalStorage REMOVE \n ERROR:',
        setLogStyle(dangerColor),
        error.message,
      );
    }
  };

  static setToLocal = (key, value) => {
    console.log(
      useLogStyle + 'LocalStorage SET ' + key + ' TO LOCAL',
      setLogStyle(infoColor),
      value,
    );
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.log(
        useLogStyle + 'LocalStorage SET ' + key + ' TO LOCAL \n ERROR:',
        setLogStyle(dangerColor),
        error.message,
      );
    }
  };

  static getFromLocal = (key) => {
    let data = null;
    try {
      let res = localStorage.getItem(key);
      data = JSON.parse(res);
      console.log(
        useLogStyle + 'LocalStorage GET ' + key + ' FROM LOCAL',
        setLogStyle(successColor),
        data,
      );
    } catch (error) {
      console.log(
        useLogStyle + 'LocalStorage GET ' + key + ' FROM LOCAL \n ERROR:',
        setLogStyle(dangerColor),
        error.message,
      );
    }

    return data;
  };

  static getStatisticDateRange = () => {
    return LocalStorage.getFromLocal('company/' + DV.company.id + '/statistic') || {};
  };

  static setStatisticDateRange = (dateRange = null) => {
    LocalStorage.setToLocal('company/' + DV.company.id + '/statistic', dateRange);
  };

  static getStaffDeviceId = () => {
    return LocalStorage.getFromLocal('deviceId') || '';
  };

  static setStaffDeviceId = (deviceId = null) => {
    LocalStorage.setToLocal('deviceId', deviceId);
  };

  static getQuickReplyData = () => {
    return LocalStorage.getFromLocal('quick_reply_data');
  };
  static setQuickReplyData = (data = null) => {
    LocalStorage.setToLocal('quick_reply_data', data);
  };

  static getRuleData = () => {
    return LocalStorage.getFromLocal('rule_data') || {};
  };
  static setRuleData = (data = {}) => {
    LocalStorage.setToLocal('rule_data', data);
  };

  static getCompany = () => {
    return LocalStorage.getFromLocal('company') || {};
  };
  static setCompany = (data = null) => {
    LocalStorage.setToLocal('company', data);
  };

  static getCurrency = () => {
    let value = LocalStorage.getFromLocal('currency');
    return value || 'VND';
  };
  static setCurrency = (data = null) => {
    LocalStorage.setToLocal('currency', data);
  };

  static getBroadcastData = () => {
    return LocalStorage.getFromLocal('broadcast_data');
  };
  static setBroadcastData = (data = null) => {
    LocalStorage.setToLocal('broadcast_data', data);
  };

  static getToken = () => {
    return LocalStorage.getFromLocal('token');
  };
  static setToken = (token = null) => {
    LocalStorage.setToLocal('token', token);
  };

  static getLanguage = () => {
    return LocalStorage.getFromLocal('language');
  };

  static setLanguage = (language = 'vi') => {
    LocalStorage.setToLocal('language', language);
  };

  static setFakeOwnerId = (id) => {
    LocalStorage.setToLocal('doopage_fake_owner_id', parseInt(id));
  };

  static setUser = (data = {}) => {
    LocalStorage.setToLocal('user', data);
  };

  static getParentNodes = () => {
    return LocalStorage.getFromLocal('company/' + DV.company.id + '/parentNodes') || [];
  };

  static setParentNodes = (data = null) => {
    LocalStorage.setToLocal('company/' + DV.company.id + '/parentNodes', data);
  };

  static getPermanentIP = () => {
    return LocalStorage.getFromLocal('ipInfo_v2') || {};
  };

  static setPermanentIP = (data = null) => {
    LocalStorage.setToLocal('ipInfo_v2', data);
  };

  static setCrefCode = (data = null) => {
    LocalStorage.setToLocal('cref_code', data);
  };

  // Mirror a referral code (from the `dpcref` query param) into the shared cookie
  // so it stays consistent with what the core service drops, other doopage.com
  // pages see it, and a later param-less reload doesn't revert to a stale cookie.
  static writeCrefCookie = ({ code, expire } = {}) => {
    try {
      if (!code || !expire) return;
      const maxAge = Number(expire) - Math.floor(Date.now() / 1000);
      if (maxAge <= 0) return;

      const host = window.location.hostname;
      const domain =
        host === 'doopage.com' || host.endsWith('.doopage.com') ? '.doopage.com' : host;
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        `dpcref=${encodeURIComponent(`${code}|${expire}`)}` +
        `; Max-Age=${maxAge}; path=/; domain=${domain}${secure}`;
    } catch (error) {
      console.log(
        useLogStyle + 'LocalStorage writeCrefCookie \n ERROR:',
        setLogStyle(dangerColor),
        error.message,
      );
    }
  };

  // The referral link (served from the core service host) drops a `dpcref` cookie
  // ("<code>|<expire unix seconds>") on the shared `.doopage.com` parent domain
  // so a different subdomain can read it. Copy it into localStorage; the cookie
  // is left in place to expire on its own since other pages may still read it.
  // Only used as a fallback when the `dpcref` query param is absent.
  static hydrateCrefCodeFromCookie = () => {
    try {
      const match = document.cookie.match(/(?:^|; )dpcref=([^;]*)/);
      if (!match) return;

      const [code, expire] = decodeURIComponent(match[1]).split('|');
      if (!code || !expire) return;

      LocalStorage.setCrefCode({ code, expire: Number(expire) });
    } catch (error) {
      console.log(
        useLogStyle + 'LocalStorage hydrateCrefCodeFromCookie \n ERROR:',
        setLogStyle(dangerColor),
        error.message,
      );
    }
  };

  // Returns the referral code while it's still within its expire time, clearing it once expired.
  static getValidCrefCode = () => {
    const data = LocalStorage.getFromLocal('cref_code');
    if (!data?.code || !data?.expire) return null;
    if (Math.floor(Date.now() / 1000) > Number(data.expire)) {
      LocalStorage.removeLocal('cref_code');
      return null;
    }
    return data.code;
  };
}
